import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { propertiesAPI } from '../api';
import { Home, BedDouble, MapPin, PoundSterling, ArrowUpRight, Filter, Sparkles } from 'lucide-react';

// Default fallback when a listing has no image (Paisley town hall / abbey skyline)
const FALLBACK_IMAGE = '/property-placeholder.jpg';
// Empty-state artwork shown when there are zero active listings overall
const NO_PROPERTIES_IMAGE = 'https://customer-assets.emergentagent.com/job_5c7ee48a-ca63-4704-82af-18d72800934b/artifacts/4q727na5_no-flat-images.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtPrice = (n) => (typeof n === 'number' && n > 0 ? `£${n.toLocaleString()} pcm` : 'Price on request');

const resolveImg = (url) => {
  if (!url) return `${BACKEND_URL}${FALLBACK_IMAGE}`;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const PropertiesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: 'all', beds: 'all' });

  useEffect(() => {
    propertiesAPI
      .list({ limit: 200 })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (filter.type !== 'all' && (p.property_type || '').toLowerCase() !== filter.type) return false;
      if (filter.beds !== 'all') {
        if (filter.beds === '4+') {
          if (!p.bedrooms || p.bedrooms < 4) return false;
        } else if ((p.bedrooms || 0) !== parseInt(filter.beds, 10)) return false;
      }
      return true;
    });
  }, [items, filter]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Flats & Houses to Let in Paisley | Live Rental Listings"
        description="Find flats and houses to let in Paisley & Renfrewshire. We search every major agent and aggregator so you don't have to — updated daily, listings link straight to source."
        keywords="Paisley flats to let, Paisley houses to rent, Renfrewshire rentals, flats Paisley, houses Paisley, lettings Paisley"
        url="/properties"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-sky-950 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-emerald-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-sky-500/20 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 text-center relative">
          <p className="text-emerald-300 text-sm font-semibold uppercase tracking-[0.2em] mb-3">Now searching every agent</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="properties-page-h1">
            Flats &amp; houses to let
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Live rental listings in and around Paisley — pulled from Rightmove, Zoopla &amp; local agents.
            Click straight through to enquire.
          </p>
          <p className="text-xs text-emerald-200/80 mt-4 max-w-xl mx-auto">
            Listings expire automatically after 10 days. If you spot an outdated entry, please let us know.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 mr-2">
            <Filter size={15} /> Filter
          </span>
          {[
            { v: 'all', l: 'All types' },
            { v: 'flat', l: 'Flats' },
            { v: 'house', l: 'Houses' },
            { v: 'studio', l: 'Studios' },
            { v: 'room', l: 'Rooms' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              data-testid={`filter-type-${opt.v}`}
              onClick={() => setFilter((f) => ({ ...f, type: opt.v }))}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                filter.type === opt.v
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
              }`}
            >
              {opt.l}
            </button>
          ))}
          <span className="w-px h-5 bg-slate-200 mx-1" />
          {[
            { v: 'all', l: 'Any beds' },
            { v: '1', l: '1' },
            { v: '2', l: '2' },
            { v: '3', l: '3' },
            { v: '4+', l: '4+' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              data-testid={`filter-beds-${opt.v}`}
              onClick={() => setFilter((f) => ({ ...f, beds: opt.v }))}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                filter.beds === opt.v
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
              }`}
            >
              {opt.l}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-500">
            {loading ? 'Loading…' : `${filtered.length} listing${filtered.length === 1 ? '' : 's'}`}
          </span>
        </div>
      </section>

      {/* Listings */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-72 bg-slate-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="properties-empty-state">
              {items.length === 0 ? (
                <>
                  <img
                    src={NO_PROPERTIES_IMAGE}
                    alt="Property image coming soon — no rentals listed at the moment"
                    className="mx-auto max-w-md w-full rounded-3xl shadow-xl shadow-emerald-500/10 ring-1 ring-slate-200/50 mb-6"
                    loading="lazy"
                  />
                  <p className="text-slate-700 text-lg font-semibold mb-1">No live listings right now</p>
                  <p className="text-slate-500 max-w-md mx-auto">
                    We're between feeds — check back soon. We update from Rightmove, Zoopla &amp; local agents most days.
                  </p>
                </>
              ) : (
                <>
                  <Home size={36} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-600 mb-2">No listings match your filter right now.</p>
                  <button onClick={() => setFilter({ type: 'all', beds: 'all' })} className="text-emerald-600 font-medium text-sm">
                    Clear filters
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
              {filtered.map((p) => (
                <Link
                  key={p.id}
                  to={`/properties/${p.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-emerald-300 hover:shadow-xl transition-all"
                  data-testid={`property-card-${p.slug}`}
                >
                  <div className="relative h-48 bg-slate-100 overflow-hidden">
                    <img
                      src={resolveImg(p.image_url)}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        if (!e.target.dataset.fallback) {
                          e.target.dataset.fallback = '1';
                          e.target.src = `${BACKEND_URL}${FALLBACK_IMAGE}`;
                        }
                      }}
                    />
                    <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-slate-800 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
                      {p.property_type}
                    </span>
                    {p.featured && (
                      <span className="absolute top-3 right-3 bg-fuchsia-500 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                        <Sparkles size={11} /> Featured
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <p className="text-xl font-extrabold text-slate-900 tracking-tight">
                        {fmtPrice(p.monthly_rent)}
                      </p>
                      <ArrowUpRight size={16} className="text-slate-400 group-hover:text-emerald-600 transition-colors" />
                    </div>
                    <h3 className="text-base font-semibold text-slate-800 leading-snug line-clamp-2 group-hover:text-emerald-700">
                      {p.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-slate-500">
                      {typeof p.bedrooms === 'number' && p.bedrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <BedDouble size={12} /> {p.bedrooms} bed{p.bedrooms === 1 ? '' : 's'}
                        </span>
                      )}
                      {p.area && (
                        <span className="flex items-center gap-1 truncate">
                          <MapPin size={12} /> <span className="truncate">{p.area}</span>
                        </span>
                      )}
                      {p.agent && (
                        <span className="truncate text-slate-400">via {p.agent}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertiesPage;
